import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Check } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

const WhyUsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const objections = [
    {
      question: "I don't want another software to learn.",
      answer:
        "Zero Learning Curve. We do not sell software. We build a pipeline between the tools you already use—WhatsApp and Excel. Your workflow doesn't change; it just gets faster. Your team keeps doing what they're doing. The magic happens invisibly.",
      highlight: "No training required. No dashboards. No logins.",
    },
    {
      question: "Is my data safe? Where does it go?",
      answer:
        "100% Data Sovereignty. We don't store your data—ever. We simply direct it to your own secure Google Drive or OneDrive. You own the pipe and the water. Your files stay on your infrastructure, protected by your existing security policies.",
      highlight: "Your data never touches third-party servers.",
    },
    {
      question: "My clients are not tech-savvy at all.",
      answer:
        'If they can send a "Hi" on WhatsApp, they can use this system. There\'s nothing new for your clients to learn. They keep sending documents the way they always have. The intelligence is on our side, not theirs.',
      highlight: "Zero change for your clients.",
    },
    {
      question: "What if something goes wrong?",
      answer:
        "Every document that enters the system is logged with a timestamp in your Master Inward Register. You have complete visibility and a perfect audit trail. Plus, our team provides dedicated support to ensure everything runs smoothly.",
      highlight: "Complete transparency with full audit trail.",
    },
    {
      question: "How is this different from hiring another Article?",
      answer:
        "An Article can process maybe 50-100 documents a day, makes errors, takes leaves, and needs supervision. Our system processes unlimited documents 24/7, never makes naming errors, and doesn't ask for increments. It's not a replacement—it's capacity multiplication.",
      highlight: "Works 24/7. Zero errors. No supervision needed.",
    },
  ];

  return (
    <section
      id="why-us"
      ref={ref}
      className="py-20 md:py-32 bg-white relative"
      data-testid="why-us-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="lg:sticky lg:top-32"
          >
            <p className="font-body text-sm font-semibold uppercase tracking-widest text-gold mb-4">
              Overcoming Your Concerns
            </p>
            <h2
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-tight tracking-tight mb-6"
              data-testid="why-us-headline"
            >
              Why InwardSync?
            </h2>
            <p className="font-body text-base md:text-lg text-slate-muted leading-relaxed mb-8">
              We understand your hesitation. You've seen "solutions" before that 
              created more problems than they solved. Here's why this is different.
            </p>

            {/* Key Points */}
            <div className="space-y-4">
              {[
                "No software to install or learn",
                "Your data stays on YOUR servers",
                "Works with tools you already use",
                "Zero change required from clients",
              ].map((point, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3"
                  data-testid={`why-us-point-${index}`}
                >
                  <div className="w-6 h-6 bg-navy rounded-full flex items-center justify-center flex-shrink-0">
                    <Check size={14} className="text-white" />
                  </div>
                  <span className="font-body text-slate-main font-medium">
                    {point}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right - Accordion */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Accordion
              type="single"
              collapsible
              className="space-y-4"
              data-testid="why-us-accordion"
            >
              {objections.map((item, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-cream border border-black/5 rounded-xl px-6 data-[state=open]:shadow-[0_8px_30px_rgba(0,0,0,0.06)]"
                  data-testid={`accordion-item-${index}`}
                >
                  <AccordionTrigger className="font-heading text-lg font-bold text-navy hover:no-underline py-5">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="pb-5">
                    <p className="font-body text-slate-muted leading-relaxed mb-4">
                      {item.answer}
                    </p>
                    <div className="bg-navy/5 rounded-lg p-3">
                      <p className="font-body text-sm font-semibold text-navy flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-gold rounded-full" />
                        {item.highlight}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WhyUsSection;
